import type { MuRosterMember } from "@shared/types";

/**
 * The pill a member is on, and the penalty dragging them down.
 *
 * The panel has shown these on every row since buffs existed; the board showed
 * neither, so the same member read as two different fighters depending on which
 * screen you were looking at. The damage figure beside them already includes the
 * effect — what the chip adds is WHEN that figure moves, which is the difference
 * between leaning on somebody now and leaning on them in an hour.
 *
 * Line for line the same rule as MUQ_FORMAT.effectLabel in the extension's
 * format.js, including dropping an effect whose clock has run out (the damage
 * figure no longer reflects it) and writing a whole number of hours as "3h".
 */
export interface Effect {
  /** Every code the game gave, for the tooltip. */
  name: string;
  /** What the chip says: the percentage, or the code when there is no figure. */
  text: string;
  /** The percentage itself, so a caller sorting or comparing effects does not
   * have to parse the words back out of the chip. */
  percent: number;
  /** The figure alone, without the countdown. The two are rendered separately so
   * that a narrow column clips the time and never the number — a "-60%" cut down
   * to "-6" is not a smaller truth, it is a different one. */
  figure: string;
  /** How long is left, already formatted, or null when the game did not say. */
  left: string | null;
  /** Under twenty minutes: whoever is planning the queue should know the figure
   * beside this member is about to move. */
  expiringSoon: boolean;
}

function effect(
  codes: string[] | undefined,
  percent: number,
  endsAt: string | null | undefined,
  nowMs: number,
  sign: string,
): Effect | null {
  if (!codes || codes.length === 0) return null;
  const endsMs = endsAt ? Date.parse(endsAt) : NaN;
  let left: string | null = null;
  if (Number.isFinite(endsMs)) {
    const minutes = Math.round((endsMs - nowMs) / 60000);
    if (minutes <= 0) return null; // over; the damage figure no longer reflects it
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    left = minutes < 60 ? `${minutes}m` : rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
  }
  const figure = percent > 0 ? `${sign}${percent}%` : codes[0];
  return {
    name: codes.join(", "),
    percent,
    text: figure + (left ? ` ${left}` : ""),
    figure,
    left,
    expiringSoon: Number.isFinite(endsMs) && endsMs - nowMs < 20 * 60000,
  };
}

/** What is lifting this member's damage, if anything. */
export function pillLabel(member: MuRosterMember, nowMs: number): Effect | null {
  return effect(member.buffCodes, Number(member.buffPercent) || 0, member.buffEndsAt, nowMs, "+");
}

/** What is dragging it down, if anything. */
export function debuffLabel(member: MuRosterMember, nowMs: number): Effect | null {
  return effect(
    member.debuffCodes,
    Number(member.debuffPercent) || 0,
    member.debuffEndsAt,
    nowMs,
    "-",
  );
}
